import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import envDetect from '@tcc/env-detect';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { determineEnv } from 'util/env';
import { getMessage } from 'util/locale';
import Date from '../Date';
import { selectCustomChemical, fetchFertilizerProducts, fetchCropProtectionProducts } from '../../products';
import { applicationSubTypes, applicationTypes, scriptTypes } from '../../constants/scripts';
import {
  protectionApplicationTypes,
  prodChemicalIds,
  qaChemicalIds,
  experimentChemicalIds
} from '../../constants/activities';
import { APPLICATION_FORMAT } from 'constants/values';
import { getActiveScript, getActiveUserId, isFieldOwner, getActiveScriptType } from '../../current/selectors';
import { updatePlantingPlanParams } from '../index';
import { getPlantingPlanParams } from '../selectors';
import { ApplicationFormatToggle } from './ApplicationFormatToggle';
import { ChemicalTypeSelector } from './ChemicalTypeSelector';
import { getCurrentPath, isEditRoute as isEditRouteFunc } from '../../routes/selectors';
import { setProductInput } from '../../current/uiwidgets/product-inputs/index';
import { setProductsForScript, setProtectionProductForScript } from '../../current/uiwidgets';
import { getIncorporationMethodForZones } from 'scripts/zones/productApplication-selectors';
import { getSubstanceTypesForApplicationType } from '../../products/fertility-selectors';
import Tag from '../Tag';
import ScriptName from 'common/ScriptName';
import Explainer from 'planting-plan-form/Explainer';
import HtmlText from 'html-text/HtmlText';
import { toLower, uniqBy, first, size } from 'lodash';


const baseString = 'vra-manual-planting-plan-form-inputs';
const classString = classNames({
  [`${baseString}`]: true,
  'vra-planting-plan-form-inputs': true
});

const defaultChemicalTypeIds = determineEnv() === envDetect.environments.PRODUCTION ? prodChemicalIds : qaChemicalIds;

function _makeGetterOfProductsByChemicalType(
  fungicideProducts, herbicideProducts, insecticideProducts, growthRegulatorProducts
) {
  return (type) => {
    switch (type) {
      case protectionApplicationTypes.APPLICATION_FUNGICIDE:
        return fungicideProducts;
      case protectionApplicationTypes.APPLICATION_HERBICIDE:
        return herbicideProducts;
      case protectionApplicationTypes.APPLICATION_INSECTICIDE:
        return insecticideProducts;
      case protectionApplicationTypes.APPLICATION_GROWTHREGULATOR:
        return growthRegulatorProducts;
      default:
        return [];
    }
  };
}

export const ManualCropProtectionInputs = ({
  activityType,
  allProducts,
  chemicalTypeIds,
  createCustomChemicalV2,
  disabled,
  displayScriptName,
  fetchFertilizers,
  fungicideProducts,
  herbicideProducts,
  insecticideProducts,
  growthRegulatorProducts,
  isAppMode,
  maxDate,
  minDate,
  onChangeScriptName,
  onSetValue,
  onSetValueV2,
  onSelectIncorporationMethod,
  onSelectDate,
  plantingPlanParams,
  selectedProduct,
  selectedProducts,
  selectedFormat,
  onSelectApplicationCategoryV2,
  validationResult,
  isEditRoute,
  isFieldOwnerValue,
  scriptType,
  protection,
  protectionId
}) => {
  const dateValidations = get(validationResult, 'date');
  const [selectedProtection, setSelectedProtection] = useState();
  const [selectedProtectionId, setSelectedProtectionId] = useState();
  const setApplicationFormat = () => { onSelectIncorporationMethod(APPLICATION_FORMAT.sprayed); };
  const isTankMix = activityType === protectionApplicationTypes.APPLICATION_MIX;
  const isEditTankMix = isTankMix && isEditRoute;

  useEffect(() => {
    fetchFertilizers(chemicalTypeIds);
  }, []);

  const isProductType = (products, productId) => products.map(product => product.id).includes(productId);

  const getProductType = (productId) => {
    const isFungicide = isProductType(fungicideProducts, productId);
    if(isFungicide) {
      return applicationSubTypes.FUNGICIDE;
    }

    const isHerbicide = isProductType(herbicideProducts, productId);
    if(isHerbicide) {
      return applicationSubTypes.HERBICIDE;
    }

    const isInsecticide = isProductType(insecticideProducts, productId);
    if(isInsecticide) {
      return applicationSubTypes.INSECTICIDE;
    }

    const isGrowthRegulator = isProductType(growthRegulatorProducts, productId);
    if(isGrowthRegulator) {
      return applicationSubTypes.GROWTHREGULATOR;
    }
    return null;
  };

  // setup selected protection when there is only one product on the edit route
  useEffect(() => {
    if(isEditRoute && !isTankMix) {
      setSelectedProtectionId(protectionId);
      setSelectedProtection(protection);
    }
  });


  const onAddProduct = (productId) =>{
    const protectionProductsCount = size(selectedProducts, 0) + 1;

    onSetValueV2(productId, isEditRoute, !isTankMix);
    setSelectedProtection();
    setSelectedProtectionId();

    if(isEditRoute) {
      return;
    }
    if(protectionProductsCount === 1) {
      const type = getProductType(productId);
      onSelectApplicationCategoryV2(type);
    }else if (protectionProductsCount > 1) {
      onSelectApplicationCategoryV2(applicationSubTypes.MIX);
    }
  };

  const onSelectProtection = (value) => {
    const protectionId = Object.keys(chemicalTypeIds)
    .filter(chemType => getMessage(`label-text__product-${chemType}`) === value);

    setSelectedProtection(value);
    setSelectedProtectionId(first(protectionId));

    if (!isEditRoute) {
      onSelectApplicationCategoryV2(first(protectionId), isEditRoute);
    }
  };

  const onRemoveProduct =(value) =>{
    onSetValueV2(value.id, isEditRoute);

    const protectionProductsCount = size(selectedProducts, 0) - 1;

    if(isEditRoute) {
      return;
    }

    if(protectionProductsCount === 0) {
      onSelectApplicationCategoryV2();
    }else if(protectionProductsCount === 1) {
      const type = getProductType(selectedProducts.find(product => product.id !== value.id).id);
      onSelectApplicationCategoryV2(type);
    }
  };

  const onCreateChemical =(value) =>{
    createCustomChemicalV2({ ...value, isTankMix });
    setSelectedProtection();
    setSelectedProtectionId();
  };
  useEffect(() => {
    if (scriptType === scriptTypes.EXPERIMENT) {
      setApplicationFormat();
    }
  }, []);

  const inputsClassString = classNames({
    'crop-protection-input-half-width': !isEditRoute,
    'crop-protection-input-full-width': isEditRoute
  });

  const getProducts = _makeGetterOfProductsByChemicalType(
    fungicideProducts, herbicideProducts, insecticideProducts, growthRegulatorProducts);

  const chemicalTypeSelector = ChemicalTypeSelector({
    chemicalTypeIds, getProducts, isEditRoute, allProducts, onCreateChemical, isFieldOwnerValue, selectedProduct,
    selectedProtectionId, onAddProduct, selectedProtection, selectedProducts, onSelectProtection, inputsClassString,
    activityType});

  const canDeleteTag = !isEditRoute || (isEditTankMix && selectedProducts.length > 1);

  const dateFieldSection = (
    <section className='vra-planting-plan-form__fieldset vra-planting-plan-form__fieldset--date'>
      { !isEditRoute && <Date
        disabled={disabled}
        headerText={getMessage('title-text__application-date')}
        isAppMode={isAppMode}
        maxDate={maxDate}
        minDate={minDate}
        onSelectDate={onSelectDate}
        selected={plantingPlanParams.date}
        validationResult={dateValidations}
      />}
    </section>);

  return (
    <section className={classString}>
      <section>
        {isEditRoute && <ScriptName
          displayScriptName={displayScriptName}
          onChange={onChangeScriptName}
          validationResult={validationResult}
        />}
      </section>
      <section>
        {scriptType !== scriptTypes.EXPERIMENT
        && (<><Explainer>
          <HtmlText messageKey='label-text__mix--i-will-be-applying' />
        </Explainer><ApplicationFormatToggle
          isEditRoute={isEditRoute}
          onSelect={onSelectIncorporationMethod}
          selected={selectedFormat}/></>)
        }
      </section>
      <section className={'crop-protection-inputs-container'}>
        <Explainer>
          <HtmlText messageKey='label-text__select-one-or-more-products-to-create-mix' />
        </Explainer>
      </section>
      <section className={classString}>
        {chemicalTypeSelector}
      </section>
      {(!isEditRoute || activityType === protectionApplicationTypes.APPLICATION_MIX)
      && <section className='selected-products'>
        {selectedProducts.length > 1
          && <div>{getMessage('crop-protection-tank-mix', {value: selectedProducts.length})}</div>}
        {selectedProducts.map(product => {
          let tagInterior = product;
          tagInterior.value = (<span>{product.value}</span>);
          return (<Tag
            canDelete={canDeleteTag}
            isCustom={product.isCustom}
            removeTag={onRemoveProduct}
            tag={tagInterior}
            key={product.id}
          />);
        })}
      </section>}
      {dateFieldSection}
    </section>
  );
};

const getSelectedProduct = (state) => {
  const plantingPlanParams = getPlantingPlanParams(state);

  return get(plantingPlanParams, `products.${applicationTypes.APPLICATIONV2}`);
};

export default connect(state => {
  const scriptType = getActiveScriptType(state);
  const plantingPlanParams = getPlantingPlanParams(state);
  const isEditRoute = isEditRouteFunc(getCurrentPath(state));
  const script = getActiveScript(state);
  const activityType = plantingPlanParams.activityType || get(script, 'activity-type', '');
  const substanceTypeNames = getSubstanceTypesForApplicationType(state, applicationTypes.APPLICATION);
  const selectedProduct = getSelectedProduct(state);

  let selectedFormat = isEditRoute
    ? getIncorporationMethodForZones(script.zones)
    : get(plantingPlanParams, `incorporationMethod`);
  let chemicalTypeIds;

  if (scriptType === scriptTypes.EXPERIMENT) {
    chemicalTypeIds = experimentChemicalIds;
  } else {
    chemicalTypeIds = substanceTypeNames.byName ? substanceTypeNames.byName : defaultChemicalTypeIds;
  }

  if (selectedFormat === 'granular') {
    selectedFormat = APPLICATION_FORMAT.spread;
  } else if (selectedFormat === 'liquid') {
    selectedFormat = APPLICATION_FORMAT.sprayed;
  }

  const productsByUuid = get(state, `products[${applicationTypes.APPLICATION}].productsByChemicalTypeUuid`);
  const allProducts = []; // we combine chemicals from both FERTILITY and CROP PROTECTION
  Object.keys(productsByUuid).forEach(key => allProducts.push(...productsByUuid[key]));

  const productsBySubstanceTypeUuid = state.products[applicationTypes.FERTILIZER].productsBySubstanceTypeUuid;
  if (productsBySubstanceTypeUuid) {
    Object.values(productsBySubstanceTypeUuid).forEach(substanceTypeId => {
      substanceTypeId.forEach(substanceId => {
        allProducts.push(substanceId);
      });
    });
  }

  const selectedProducts = allProducts
  .filter(value => selectedProduct.includes(value.id))
  .map(product => ({id: product.id, value: product.name, isValid: true, isCustom: product.isCustom}));

  const uniqueProducts = uniqBy(selectedProducts, 'id');

  const splitIndex = activityType.indexOf('-');
  let protectionId = activityType.substring(splitIndex + 1, activityType.length);
  return {
    activityType,
    allProducts,
    fungicideProducts: get(productsByUuid, chemicalTypeIds.fungicide),
    herbicideProducts: get(productsByUuid, chemicalTypeIds.herbicide),
    growthRegulatorProducts: get(productsByUuid, chemicalTypeIds.growthregulator),
    insecticideProducts: get(productsByUuid, chemicalTypeIds.insecticide),
    chemicalTypeIds,
    isEditRoute,
    selectedProduct,
    selectedProducts: uniqueProducts,
    selectedFormat,
    userId: getActiveUserId(state),
    isFieldOwnerValue: isFieldOwner(state),
    scriptType,
    protection: getMessage(`label-text__product-${protectionId}`),
    protectionId: protectionId
  };
}, dispatch => {
  return {
    fetchFertilizers: (chemicalTypeIds) => {
      dispatch(fetchFertilizerProducts({
        substanceTypeUuid: null
      }));
      dispatch(fetchCropProtectionProducts({
        chemicalTypeIds
      }));
    },
    onSetValue: (id, isEditRoute, products) => {
      if (!isEditRoute) {
        dispatch(updatePlantingPlanParams({
          products: { [applicationTypes.APPLICATION]: { 'select-1': id } }
        }));
      } else {
        const newProducts = products.filter(prods => prods.id === id);
        dispatch(
          setProductInput({
            active: true,
            applicationType: applicationTypes.APPLICATION,
            currentProductId: id,
            products: newProducts,
            selectId: 'select-1'
          })
        );
        dispatch(setProtectionProductForScript({ productId: id }));
      }
    },
    onSetValueV2: (id, isEditRoute, clearExistingProducts) => {
      if (!isEditRoute) {
        dispatch(updatePlantingPlanParams({
          products: {[applicationTypes.APPLICATIONV2]: id }
        }));
      } else {
        dispatch(setProductsForScript({
          products: {[applicationTypes.APPLICATIONV2]: id }
        }, clearExistingProducts));
        dispatch(setProtectionProductForScript({ productId: id }));
      }
    },
    onSelectApplicationCategoryV2: (activityType) => {
      dispatch(updatePlantingPlanParams({
        activityCategoryUuid: activityType,
        activityType: `application-${activityType}`
      }));
    },
    createCustomChemicalV2: ({ chemicalName, substanceId, chemicalTypeIds, isTankMix }) => {
      dispatch(selectCustomChemical({
        chemicalName,
        chemicalTypeId: chemicalTypeIds[toLower(substanceId.replace(/\s/g, ''))],
        substanceId,
        isTankMix
      }));
    }
  };
})(ManualCropProtectionInputs);

ManualCropProtectionInputs.propTypes = {
  activityType: propTypes.string,
  allProducts: propTypes.array,
  chemicalTypeIds: propTypes.object,
  createCustomChemicalV2: propTypes.func.isRequired,
  disabled: propTypes.bool.isRequired,
  displayScriptName: propTypes.string,
  fetchFertilizers: propTypes.func,
  fungicideProducts: propTypes.array,
  growthRegulatorProducts: propTypes.array,
  herbicideProducts: propTypes.array,
  insecticideProducts: propTypes.array,
  isAppMode: propTypes.bool.isRequired,
  isEditRoute: propTypes.bool.isRequired,
  isFieldOwnerValue: propTypes.bool.isRequired,
  maxDate: propTypes.string.isRequired,
  minDate: propTypes.string.isRequired,
  onChangeScriptName: propTypes.func,
  onSelectApplicationCategoryV2: propTypes.func,
  onSelectDate: propTypes.func.isRequired,
  onSelectIncorporationMethod: propTypes.func.isRequired,
  onSetValue: propTypes.func,
  onSetValueV2: propTypes.func,
  plantingPlanParams: propTypes.object.isRequired,
  protection: propTypes.string,
  protectionId: propTypes.string,
  scriptType: propTypes.string,
  selectedFormat: propTypes.string,
  selectedProduct: propTypes.object.isRequired,
  selectedProducts: propTypes.array.isRequired,
  userId: propTypes.string,
  validationResult: propTypes.object.isRequired
};
